<template>
  <div
    v-loading="eventsLoadingStatus === 'init'"
    element-loading-text="We are getting your events.."
    class="relative h-full py-15 px-20 mobile-l:py-20 mobile-l:px-25 tablet:py-25 tablet:px-35"
  >
    <UIFloatingActionButton
      v-show="FABIsVisible && eventsLoadingStatus !== 'init' && !isEmptyEvents"
      :label-is-visible="FABLabelIsVisible"
      class="bg-secondary z-2000"
      @click="onCreateEventPress"
    >
      <template slot="icon">
        <FontAwesomeIcon
          :icon="['far', 'plus']"
          class="fab-icon text-xl text-white"
        />
      </template>
      <span class="text-white">
        Create Event
      </span>
    </UIFloatingActionButton>

    <div
      v-if="isEmptyEvents && eventsLoadingStatus !== 'init'"
      class="h-full flex flex-col items-center justify-center py-20"
    >
      <img
        src="/images/icons/no-events/no-events.png"
        srcset="/images/icons/no-events/no-events.png 1x,
                /images/icons/no-events/no-events@2x.png 2x
                /images/icons/no-events/no-events@3x.png 3x"
        class="max-h-115"
        alt="No events yet"
      >
      <h2 class="text-xl text-primary my-30">Add your first event</h2>
      <button
        class="flex items-center text-white tracking-wide py-12 px-40 bg-secondary rounded shadow-2dp hover:shadow-6dp"
        @click="onCreateEventPress"
      >
        <FontAwesomeIcon
          :icon="['far', 'plus']"
          class="text-3xl text-xl text-white mr-20"
        />
        Create event
      </button>
    </div>
    <template v-else-if="eventsLoadingStatus !== 'init'">
      <h2 class="text-primary leading-tight mb-10">{{ upcomingAndOngoingEventsTitle }}</h2>
      <div class="mb-25 tablet:mb-30">
        <EventList
          v-if="ongoingEvents.length"
          :class="{ 'mb-20': ongoingEvents.length && upcomingEvents.length }"
        >
          <EventCard
            v-for="event in ongoingEvents"
            :key="event.id"
            :event="event"
            :owned-by-user="event.ownerId && event.ownerId === userAuthDataId"
            :no-upgrade="event.packageId && event.packageId === highestOneOffPackageId"
            ongoing
            :price-package="packages.find(pack => pack.id === event.packageId)"
            @delete-event="onDeleteEventPress($event)"
            @route-navigation="onRouteNavigation($event)"
          />
        </EventList>
        <EventList v-if="upcomingEvents.length">
          <EventCard
            v-for="event in upcomingEvents"
            :key="event.id"
            :event="event"
            :owned-by-user="event.ownerId && event.ownerId === userAuthDataId"
            :no-upgrade="event.packageId === highestOneOffPackageId"
            :price-package="packages.find(pack => pack.id === event.packageId)"
            @delete-event="onDeleteEventPress($event)"
            @route-navigation="onRouteNavigation($event)"
          />
        </EventList>
      </div>

      <div
        v-if="pastEvents.length"
        v-show="eventsLoadingStatus !== 'init'"
      >
        <h2 class="text-primary leading-tight mb-10">
          {{ pastEvents.length + ' past ' + eventPluralRules(pastEvents.length) }}
        </h2>
        <EventList>
          <EventCard
            v-for="event in pastEvents"
            :key="event.id"
            :event="event"
            :owned-by-user="event.ownerId && event.ownerId === userAuthDataId"
            no-upgrade
            :price-package="packages.find(pack => pack.id === event.packageId)"
            @delete-event="onDeleteEventPress($event)"
            @route-navigation="onRouteNavigation($event)"
          />
        </EventList>
      </div>
    </template>
  </div>
</template>

<script>
import LogRocket from 'logrocket';
import { mapState, mapGetters, mapActions } from 'vuex';
import { orderBy } from 'lodash-es';
import { FSGetUserEvents } from '@/helpers/firestoreRefs';
import { FBFirestoreRef } from '@/helpers/firebaseServices';
import windowScrollPosition from '@/mixins/windowScrollPosition';
import windowSize from '@/mixins/windowSize';
import eventPluralRules from '@/utils/pluralRules/event';
import { showSuccessNotification, showErrorNotification } from '@/utils/notification';
import UIFloatingActionButton from '@/components/UI/UIFloatingActionButton';
import EventList from '@/components/event/EventList';
import EventCard from '@/components/event/EventCard';
import { requestInterval, clearRequestInterval } from '@/utils/requestInterval';

const mobileLargeMinWidth = 425;
const mobileMinWidth = 320;
const upcomingOngoingEventIntervalDelay = 5000;

export default {
  components: {
    UIFloatingActionButton,
    EventList,
    EventCard,
  },
  data: () => ({
    unsubUserEvents: null,
    FABIsVisible: true,
    FABLabelIsVisible: false,
    previousWindowScrollPositionY: 0,
    nowIntervalRef: null,
    secondsNow: new Date().getTime() / 1000, // Current local time in seconds
  }),
  mixins: [
    windowSize('windowSize', 50, { maxWait: 50 }),
    windowScrollPosition('windowScrollPosition', 20, { leading: true, maxWait: 50 }),
  ],
  created() {
    this.unsubUserEvents = this.subUserEvents(this.userAuthDataId);

    this.nowIntervalRef = requestInterval(() => {
      this.secondsNow = new Date().getTime() / 1000;
    }, upcomingOngoingEventIntervalDelay);
  },
  watch: {
    'windowSize.width': {
      immediate: true,
      handler(newValue) {
        this.FABLabelIsVisible = newValue > mobileLargeMinWidth;
      },
    },
    'windowScrollPosition.y': {
      immediate: true,
      handler(newValue) {
        if (this.windowSize.width >= mobileMinWidth) {
          this.FABIsVisible = true;
          return;
        }

        this.FABIsVisible = newValue <= this.previousWindowScrollPositionY || this.previousWindowScrollPositionY <= 0;
        this.previousWindowScrollPositionY = newValue;
      },
    },
  },
  computed: {
    ...mapState('packages', {
      packages: 'packageDocs',
    }),
    ...mapState('user/events', {
      eventsLoadingStatus: 'loadingStatus',
    }),
    ...mapGetters('user/events', {
      allEventsSortedByStartingTime: 'allSortedByStartingTime',
    }),
    ...mapGetters('packages', {
      highestOneOffPackageId: 'highestOneOffPackageId',
    }),
    ...mapGetters('user/auth', {
      userAuthDataId: 'authDataId',
    }),
    ...mapGetters('user/package', {
      activeSubscriptionPlanPackage: 'activeSubscriptionPlanPackage',
    }),
    upcomingEvents() {
      return orderBy(
        this.allEventsSortedByStartingTime
          .filter((event) => event?.status === 0 && event?.startingTime?.seconds > this.secondsNow),
        [(event) => event.startingTime.seconds],
        ['asc'],
      );
    },
    ongoingEvents() {
      return orderBy(
        this.allEventsSortedByStartingTime
          .filter((event) => event.status === 0 && event?.startingTime?.seconds <= this.secondsNow),
        [(event) => event.startingTime.seconds],
        ['asc'],
      );
    },
    pastEvents() {
      return orderBy(
        this.allEventsSortedByStartingTime.filter((event) => event.status === 1),
        [(event) => event.startingTime.seconds],
        ['desc'],
      );
    },
    isEmptyEvents() {
      return this.ongoingEvents.length === 0 && this.upcomingEvents.length === 0 && this.pastEvents.length === 0;
    },
    upcomingAndOngoingEventsTitle() {
      if (this.ongoingEvents.length && this.upcomingEvents.length) {
        return `${this.ongoingEvents.length} ongoing & ${this.upcomingEvents.length} upcoming events`;
      }
      if (this.ongoingEvents.length === 0 && this.upcomingEvents.length === 0 && this.pastEvents.length === 0) {
        return 'No events yet';
      }
      if (this.ongoingEvents.length === 0 && this.upcomingEvents.length === 0) {
        return 'No ongoing/upcoming event';
      }
      if (this.upcomingEvents.length === 0) {
        return `${this.ongoingEvents.length} ongoing ${eventPluralRules(this.ongoingEvents.length)}`;
      }

      return `${this.upcomingEvents.length} upcoming ${eventPluralRules(this.upcomingEvents.length)}`;
    },
  },
  methods: {
    ...mapActions('user/events', {
      onUserEventsSnapshot: 'onEventsSnapshot',
    }),
    subUserEvents(userId) {
      return FSGetUserEvents(userId)
        .onSnapshot((snapshot) => {
          this.onUserEventsSnapshot(snapshot);
          this.backfillOwnerEmail(snapshot, userId);
        }, (error) => {
          LogRocket.captureException(error);
          console.error(error);
        });
    },
    // Events only store owner.id/name and team members cannot read the owner's user document, so shared events
    // could only say "Shared by <name>". When the owner loads a shared event (team > 1) without owner.email, store
    // it once so members see who shared it. Owner-only write, maxGuests untouched (Firestore rules require that).
    backfillOwnerEmail(snapshot, userId) {
      const email = this.$store.state.user?.auth?.authData?.email;
      if (!email) return;

      snapshot.docs.forEach((doc) => {
        const event = doc.data();
        if (event.ownerId !== userId || (event.team || []).length < 2 || event.owner?.email) return;

        doc.ref.update({ 'owner.email': email }).catch((error) => console.error(error));
      });
    },
    eventPluralRules(eventCount) {
      return eventPluralRules(eventCount);
    },
    handleFreeEventLimitReached() {
      const freeUpcomingOngoingEventCount = this.ongoingEvents.filter((event) => !event.packageId).length
        + this.upcomingEvents.filter((event) => !event.packageId).length;

      if (!freeUpcomingOngoingEventCount || this.activeSubscriptionPlanPackage) return false;

      this.$alert('You can only have one free upcoming event, please upgrade your Events or become a subscriber.', 'Limit of one free Event', {
        confirmButtonText: 'Subscribe now',
        closeOnPressEscape: true,
        callback: (action) => {
          if (action !== 'confirm') return;

          this.$router.push({ name: 'SubscriptionPlans' });
        },
      }).catch((error) => error);
      return true;
    },
    onRouteNavigation({ action, eventId }) {
      if (action === 'details') {
        this.$router.push({ name: 'EventDetails', params: { eventID: eventId } });
      } else if (action === 'update') {
        this.$router.push({ name: 'EventUpdate', params: { eventID: eventId } });
      } else if (action === 'upgrade') {
        this.$router.push({ name: 'EventUpgrade', params: { eventID: eventId } });
      }
    },
    onCreateEventPress() {
      if (this.handleFreeEventLimitReached()) return;

      this.$router.push({ name: 'EventCreate' });
    },
    onDeleteEventPress(eventId) {
      this.$confirm(`You are about to delete this event. If deleted you will not be able to restore it.
      <br>In case you want to delete the guests,
      <a href="https://help.eventor.app/en/articles/2645682-deleting-guests" target="_blank" class="font-bold">
      read this article
      </a>`, 'Warning', {
        confirmButtonText: 'Delete event',
        cancelButtonText: 'Cancel',
        cancelButtonClass: 'cancel-button',
        confirmButtonClass: 'confirm-button',
        type: 'error',
        dangerouslyUseHTMLString: true,
      }).then(() => {
        FBFirestoreRef
          .collection('events')
          .doc(eventId)
          .set({ status: 2 }, { merge: true })
          .then(() => {
            showSuccessNotification('Trashed the Event successfully.');
          })
          .catch((error) => {
            showErrorNotification(error);
            console.error(error);
          });
      }).catch((error) => error);
    },
  },
  beforeDestroy() {
    if (this.unsubUserEvents !== null) this.unsubUserEvents();

    if (this.nowIntervalRef !== null) clearRequestInterval(this.nowIntervalRef);
  },
};
</script>

<style scoped>
.fab-icon {
  @apply w-30 !important;
  @apply h-30;
}

</style>
